import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, CalendarBlank, User, Stethoscope, Pill, Warning, Brain, VideoCamera, Paperclip, X, Download, UploadSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseDetail() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opinionForm, setOpinionForm] = useState({
    diagnosis_confirmation: '',
    alternative_treatments: '',
    surgery_necessity: '',
    risk_benefit_analysis: '',
    prognosis: '',
    follow_up_recommendations: '',
  });
  const [submittingOpinion, setSubmittingOpinion] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [opinionFiles, setOpinionFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchCaseDetail();
  }, [caseId]);

  const fetchCaseDetail = async () => {
    try {
      const { data } = await axios.get(`${API}/cases/${caseId}`, { withCredentials: true });
      setCaseData(data);
    } catch (error) {
      toast.error('Failed to fetch case details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleOpinionChange = (e) => {
    setOpinionForm({ ...opinionForm, [e.target.name]: e.target.value });
  };

  const handleSubmitOpinion = async (e) => {
    e.preventDefault();
    setSubmittingOpinion(true);

    try {
      await axios.post(`${API}/cases/${caseId}/opinion`, opinionForm, { withCredentials: true });
      toast.success('Opinion submitted successfully!');
      fetchCaseDetail();
    } catch (error) {
      toast.error('Failed to submit opinion');
    } finally {
      setSubmittingOpinion(false);
    }
  };

  const handleOpinionFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingFile(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(
          `${API}/cases/${caseId}/opinion-files`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
        );
        setOpinionFiles((prev) => [...prev, data]);
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploadingFile(false);
    e.target.value = '';
    fetchCaseDetail();
  };

  const downloadOpinionFile = async (filePath, filename) => {
    try {
      const resp = await axios.get(`${API}/files/${filePath}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'prescription';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleAIAnalysis = async () => {
    setGeneratingAI(true);
    toast.info('Generating AI preliminary analysis with Claude...');

    try {
      const { data } = await axios.post(
        `${API}/cases/${caseId}/ai-analysis`,
        {},
        { withCredentials: true }
      );
      
      setOpinionForm({
        diagnosis_confirmation: data.diagnosis_confirmation || '',
        alternative_treatments: data.alternative_treatments || '',
        surgery_necessity: data.surgery_necessity?.split(' ')[0] || '',
        risk_benefit_analysis: data.risk_benefit_analysis || '',
        prognosis: data.prognosis || '',
        follow_up_recommendations: data.follow_up_recommendations || '',
      });
      
      toast.success('AI analysis generated! Please review and refine before submitting.');
    } catch (error) {
      toast.error('Failed to generate AI analysis');
    } finally {
      setGeneratingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-secondary">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen pt-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-secondary">Loading case details...</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusLabel = (status) => {
    const labels = {
      submitted: 'Submitted',
      under_review: 'Under Review',
      opinion_ready: 'Opinion Ready',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="case-detail-page">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="text-primary hover:underline mb-2 text-sm"
                data-testid="back-button"
              >
                ← Back
              </button>
              <h1 className="font-manrope text-3xl font-bold text-primary">Case Details</h1>
            </div>
            <span className={`status-badge status-${caseData?.status}`}>
              {getStatusLabel(caseData?.status)}
            </span>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Case Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Patient Info */}
              <div className="card">
                <h2 className="font-manrope text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                  <User size={24} weight="duotone" />
                  Patient Information
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-secondary mb-1">Name</p>
                    <p className="font-medium text-primary">{caseData?.patient_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary mb-1">Email</p>
                    <p className="font-medium text-primary">{caseData?.patient_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary mb-1">Specialty</p>
                    <p className="font-medium text-primary">{caseData?.specialty}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary mb-1">Tier</p>
                    <p className="font-medium text-primary capitalize">{caseData?.tier}</p>
                  </div>
                </div>
              </div>

              {/* Medical Details */}
              <div className="card">
                <h2 className="font-manrope text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                  <Stethoscope size={24} weight="duotone" />
                  Medical Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-secondary mb-1">Current Diagnosis</p>
                    <p className="text-primary">{caseData?.current_diagnosis}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary mb-1">Symptoms</p>
                    <p className="text-primary">{caseData?.symptoms}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary mb-1">Duration</p>
                    <p className="text-primary">{caseData?.duration}</p>
                  </div>
                  {caseData?.medications && (
                    <div>
                      <p className="text-xs text-secondary mb-1 flex items-center gap-1">
                        <Pill size={16} /> Current Medications
                      </p>
                      <p className="text-primary">{caseData.medications}</p>
                    </div>
                  )}
                  {caseData?.allergies && (
                    <div>
                      <p className="text-xs text-secondary mb-1 flex items-center gap-1">
                        <Warning size={16} /> Allergies
                      </p>
                      <p className="text-primary">{caseData.allergies}</p>
                    </div>
                  )}
                  {caseData?.medical_history && (
                    <div>
                      <p className="text-xs text-secondary mb-1">Medical History</p>
                      <p className="text-primary">{caseData.medical_history}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Medical Files */}
              {caseData?.files && caseData.files.length > 0 && (
                <div className="card">
                  <h2 className="font-manrope text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                    <FileText size={24} weight="duotone" />
                    Medical Records
                  </h2>
                  <div className="space-y-2">
                    {caseData.files.map((file, idx) => (
                      <a
                        key={idx}
                        href={`${API}/files/${file.storage_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-surface-secondary rounded-lg hover:bg-primary/5 transition-colors"
                        data-testid={`file-link-${idx}`}
                      >
                        <p className="text-primary font-medium">{file.original_filename}</p>
                        <p className="text-xs text-secondary">
                          {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploaded_at).toLocaleDateString()}
                        </p>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Doctor's Opinion Form (Only for doctors) */}
              {user?.role === 'doctor' && caseData?.status !== 'opinion_ready' && (
                <div className="card">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="font-manrope text-xl font-semibold text-primary">Submit Medical Opinion</h2>
                    <button
                      type="button"
                      onClick={handleAIAnalysis}
                      disabled={generatingAI}
                      className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-full font-medium hover:bg-accent/90 disabled:opacity-50 transition-all text-sm"
                      data-testid="ai-analysis-button"
                    >
                      <Brain size={18} weight="duotone" />
                      {generatingAI ? 'Analyzing...' : 'AI Preliminary Analysis'}
                    </button>
                  </div>
                  <div className="bg-surface-secondary border border-accent/30 rounded-lg p-3 mb-4 text-xs text-secondary">
                    <strong className="text-accent">AI Assistant:</strong> Click "AI Preliminary Analysis" to generate a draft opinion using Claude Sonnet 4.5. Always review and refine the analysis before submitting - you retain full medical responsibility.
                  </div>
                  <form onSubmit={handleSubmitOpinion} className="space-y-4" data-testid="opinion-form">
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Diagnosis Confirmation/Correction
                      </label>
                      <textarea
                        name="diagnosis_confirmation"
                        value={opinionForm.diagnosis_confirmation}
                        onChange={handleOpinionChange}
                        rows={3}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        required
                        data-testid="diagnosis-confirmation-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Alternative Treatment Options
                      </label>
                      <textarea
                        name="alternative_treatments"
                        value={opinionForm.alternative_treatments}
                        onChange={handleOpinionChange}
                        rows={3}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        required
                        data-testid="alternative-treatments-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Surgery Necessity Assessment
                      </label>
                      <select
                        name="surgery_necessity"
                        value={opinionForm.surgery_necessity}
                        onChange={handleOpinionChange}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        required
                        data-testid="surgery-necessity-select"
                      >
                        <option value="">Select...</option>
                        <option value="Yes">Yes - Surgery is necessary</option>
                        <option value="No">No - Surgery is not necessary</option>
                        <option value="Conditional">Conditional - Depends on factors</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Risk-Benefit Analysis
                      </label>
                      <textarea
                        name="risk_benefit_analysis"
                        value={opinionForm.risk_benefit_analysis}
                        onChange={handleOpinionChange}
                        rows={3}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        required
                        data-testid="risk-benefit-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Prognosis & Recovery Timeline
                      </label>
                      <textarea
                        name="prognosis"
                        value={opinionForm.prognosis}
                        onChange={handleOpinionChange}
                        rows={2}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        required
                        data-testid="prognosis-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary mb-1">
                        Follow-up Recommendations
                      </label>
                      <textarea
                        name="follow_up_recommendations"
                        value={opinionForm.follow_up_recommendations}
                        onChange={handleOpinionChange}
                        rows={2}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        required
                        data-testid="follow-up-input"
                      />
                    </div>

                    {/* File upload for handwritten prescription / supporting docs */}
                    <div className="border-t border-primary/10 pt-4">
                      <label className="block text-sm font-medium text-secondary mb-2 flex items-center gap-2">
                        <Paperclip size={16} weight="bold" />
                        Attach Handwritten Prescription / Supporting Documents
                        <span className="text-xs text-secondary font-normal">(PDF / JPG / PNG — optional)</span>
                      </label>
                      <label
                        htmlFor="opinion-file-input"
                        className={`flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-primary/30 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all ${uploadingFile ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid="opinion-file-dropzone"
                      >
                        <UploadSimple size={18} weight="bold" className="text-primary" />
                        <span className="text-sm text-primary font-medium">
                          {uploadingFile ? 'Uploading...' : 'Click to attach prescription / scans'}
                        </span>
                        <input
                          id="opinion-file-input"
                          type="file"
                          multiple
                          accept="image/*,application/pdf"
                          onChange={handleOpinionFileUpload}
                          disabled={uploadingFile}
                          className="hidden"
                          data-testid="opinion-file-input"
                        />
                      </label>
                      {(caseData?.opinion?.files?.length > 0 || opinionFiles.length > 0) && (
                        <div className="mt-3 space-y-2" data-testid="opinion-files-list">
                          {(caseData?.opinion?.files || []).map((f, i) => (
                            <div
                              key={f.file_id || i}
                              className="flex items-center justify-between gap-3 px-3 py-2 bg-primary/5 rounded-lg"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Paperclip size={14} weight="bold" className="text-primary flex-shrink-0" />
                                <span className="text-sm text-primary truncate">{f.original_filename}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => downloadOpinionFile(f.storage_path, f.original_filename)}
                                className="text-xs text-accent hover:underline flex items-center gap-1"
                              >
                                <Download size={12} weight="bold" /> View
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submittingOpinion}
                      className="w-full btn-primary py-3 disabled:opacity-50"
                      data-testid="submit-opinion-button"
                    >
                      {submittingOpinion ? 'Submitting...' : 'Submit Opinion'}
                    </button>
                  </form>
                </div>
              )}

              {/* Display Opinion (if ready) */}
              {caseData?.opinion && (
                <>
                  {/* Case-to-Video Upgrade CTA (only for patient when basic tier) */}
                  {user?.role === 'patient' && caseData?.tier === 'basic' && (
                    <div
                      className="card mb-6"
                      style={{ background: 'linear-gradient(135deg, #D48C70 0%, #C17B5F 100%)', color: 'white' }}
                      data-testid="upgrade-to-video-card"
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                          <div className="flex items-center gap-2 mb-2">
                            <VideoCamera size={24} weight="duotone" />
                            <h3 className="font-manrope text-lg font-semibold">Want to discuss this opinion in detail?</h3>
                          </div>
                          <p className="text-sm text-white/90 leading-relaxed">
                            Book a 30-minute video call with {caseData?.opinion?.doctor_name || 'the same specialist'} to discuss your diagnosis, ask questions, and clarify next steps.
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold font-manrope">+₹2,500</p>
                          <p className="text-xs text-white/80">30-min video consult</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const doctorId = caseData?.opinion?.doctor_id;
                          if (doctorId) navigate(`/doctors/${doctorId}/book`);
                          else navigate('/doctors');
                        }}
                        className="mt-4 w-full sm:w-auto px-6 py-2.5 bg-white text-accent rounded-full font-semibold text-sm hover:bg-white/90"
                        data-testid="book-video-upgrade-button"
                      >
                        Book Video Discussion →
                      </button>
                    </div>
                  )}

                  <div className="card bg-surface" data-testid="opinion-display">
                  <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                    <h2 className="font-manrope text-xl font-semibold text-primary">Medical Opinion</h2>
                    {caseData?.ai_analysis && (
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#E1EDE5', color: '#3B6953', border: '1px solid #3B6953' }}
                        data-testid="trust-badge"
                        title="This case was first analyzed by Claude AI and then independently verified by a specialist registered with the NMC or a State Medical Council"
                      >
                        <Brain size={16} weight="duotone" />
                        <span>AI-Assisted + Specialist Verified</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-secondary mb-1">Reviewed by</p>
                      <p className="font-medium text-primary">{caseData.opinion?.doctor_name || 'Specialist'}</p>
                      {caseData.opinion?.submitted_at && (
                        <p className="text-xs text-secondary">
                          {new Date(caseData.opinion.submitted_at).toLocaleString()}
                        </p>
                      )}
                    </div>

                    {caseData.opinion?.diagnosis_confirmation && (
                      <div>
                        <p className="text-sm font-semibold text-primary mb-1">Diagnosis Confirmation</p>
                        <p className="text-secondary whitespace-pre-wrap">{caseData.opinion.diagnosis_confirmation}</p>
                      </div>
                    )}

                    {caseData.opinion?.alternative_treatments && (
                      <div>
                        <p className="text-sm font-semibold text-primary mb-1">Alternative Treatments</p>
                        <p className="text-secondary whitespace-pre-wrap">{caseData.opinion.alternative_treatments}</p>
                      </div>
                    )}

                    {caseData.opinion?.surgery_necessity && (
                      <div>
                        <p className="text-sm font-semibold text-primary mb-1">Surgery Necessity</p>
                        <p className="text-secondary">{caseData.opinion.surgery_necessity}</p>
                      </div>
                    )}

                    {caseData.opinion?.risk_benefit_analysis && (
                      <div>
                        <p className="text-sm font-semibold text-primary mb-1">Risk-Benefit Analysis</p>
                        <p className="text-secondary whitespace-pre-wrap">{caseData.opinion.risk_benefit_analysis}</p>
                      </div>
                    )}

                    {caseData.opinion?.prognosis && (
                      <div>
                        <p className="text-sm font-semibold text-primary mb-1">Prognosis</p>
                        <p className="text-secondary whitespace-pre-wrap">{caseData.opinion.prognosis}</p>
                      </div>
                    )}

                    {caseData.opinion?.follow_up_recommendations && (
                      <div>
                        <p className="text-sm font-semibold text-primary mb-1">Follow-up Recommendations</p>
                        <p className="text-secondary whitespace-pre-wrap">{caseData.opinion.follow_up_recommendations}</p>
                      </div>
                    )}

                    {/* Doctor-attached prescription / supporting docs */}
                    {caseData.opinion?.files?.length > 0 && (
                      <div className="border-t border-primary/10 pt-4">
                        <p className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                          <Paperclip size={16} weight="bold" />
                          Attached Prescription &amp; Documents
                        </p>
                        <div className="space-y-2" data-testid="patient-opinion-files">
                          {caseData.opinion.files.map((f, i) => (
                            <button
                              key={f.file_id || i}
                              type="button"
                              onClick={() => downloadOpinionFile(f.storage_path, f.original_filename)}
                              className="w-full flex items-center justify-between gap-3 p-3 bg-surface-secondary rounded-lg hover:bg-primary/5 transition-colors text-left"
                              data-testid={`download-opinion-file-${i}`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <Paperclip size={18} weight="bold" className="text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-primary truncate font-medium">{f.original_filename}</p>
                                  <p className="text-xs text-secondary">
                                    {f.size ? `${(f.size / 1024).toFixed(1)} KB` : ''} {f.content_type || ''}
                                  </p>
                                </div>
                              </div>
                              <Download size={18} weight="bold" className="text-accent flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Timeline */}
              <div className="card">
                <h2 className="font-manrope text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <CalendarBlank size={20} weight="duotone" />
                  Timeline
                </h2>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-primary">Case Submitted</p>
                      <p className="text-xs text-secondary">
                        {new Date(caseData?.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {caseData?.status !== 'submitted' && (
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-accent mt-2"></div>
                      <div>
                        <p className="text-sm font-medium text-primary">Under Review</p>
                        <p className="text-xs text-secondary">
                          {new Date(caseData?.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                  {caseData?.status === 'opinion_ready' && (
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#3B6953' }}></div>
                      <div>
                        <p className="text-sm font-medium text-primary">Opinion Ready</p>
                        <p className="text-xs text-secondary">
                          {new Date(caseData?.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}